/**
 * SlipHeader — the ruled header band shared by browse and search (new, 2026-08).
 *
 * WHAT IT REPLACES. Both pages opened with a full-width white card carrying a
 * title and a count: on desktop that slab was ~130px of empty surface before a
 * single product appeared, and on a 390px phone it ate a fifth of the first
 * viewport to say "Каталог товаров / 421 товар" — two facts a user who tapped
 * "Магазины" already knows. It was also a nested card that then contained
 * cards, which is the surest tell of a generated layout.
 *
 * WHAT IT IS. A counter slip's header: the title in the display face (Onest), the
 * count and the controls on the same optical line, and a DRAWN rule under the
 * lot. No card, no fill, no motif band — the rule is the whole armature, which
 * is exactly the "visible armature" raise. Vertical cost on mobile drops from
 * ~130px to ~56px, so the first row of products is above the fold.
 *
 * The count sits in `tabular-nums` rather than in `.g-data`: it is a
 * localized SENTENCE ("42 товара", "1000 ашык товар", "42 products") whose word
 * order differs per language, and setting a Cyrillic word in IBM Plex Mono to
 * get four tabular digits would be monospace-as-costume. `tabular-nums` gives
 * the digits their even advance without touching the face.
 *
 * `actions` is where the mobile "Filters" trigger lands; `children` is the
 * applied-filter row, which draws its own rule so it reads as a second course
 * of the same slip. Strings come from the caller (both pages use different
 * namespaced keys for their own titles).
 */
import { type ReactElement, type ReactNode } from "react";
import { motion } from "framer-motion";

import { useGardenMotion } from "@/lib/use-garden-motion";
import { MOTION } from "@/lib/motion";

export interface SlipHeaderProps {
  /** The screen title. Rendered as the page `<h1>`. */
  title: string;
  /** Test hook for the heading (the search page asserts on `search-title`). */
  titleTestId?: string;
  /**
   * The result-count line, already localized and pluralised by the caller.
   * `null` renders the node empty rather than removing it — the search page
   * needs a STABLE live region so a polite announcement fires exactly once.
   */
  count?: string | null;
  /** Test hook for the count node (`result-count` on both pages). */
  countTestId?: string;
  /** `aria-live` on the count node — search announces, browse does not. */
  countLive?: boolean;
  /** Right-hand controls (the mobile filter trigger). */
  actions?: ReactNode;
  /** Second course: the applied-filter row. */
  children?: ReactNode;
  /** Re-runs the entrance when it changes (a new search query). */
  entranceKey?: string;
}

export function SlipHeader({
  title,
  titleTestId = "slip-title",
  count,
  countTestId,
  countLive = false,
  actions,
  children,
  entranceKey,
}: SlipHeaderProps): ReactElement {
  const { reduced } = useGardenMotion();

  return (
    <header className="flex flex-col">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 pb-3">
        {/* Only the TITLE is keyed/animated. The count node below must stay
            mounted across query changes: it is the search page's polite live
            region, and a remount would announce the same count twice. */}
        <motion.h1
          key={entranceKey}
          data-testid={titleTestId}
          className="font-display min-w-0 break-words text-xl font-bold leading-tight tracking-tight text-foreground sm:text-[1.75rem]"
          initial={reduced ? false : { opacity: 0, y: 6 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={
            reduced
              ? undefined
              : { duration: MOTION.dur.bubble, ease: MOTION.ease.out }
          }
        >
          {title}
        </motion.h1>
        {/* On a phone the title takes the first line and this group wraps to a
            full-width second line — count left, control right — instead of
            huddling together on the left with the count reading like a button
            label. From `sm` up it is a shrink-to-fit group on the title's line. */}
        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
          <p
            data-testid={countTestId}
            aria-live={countLive ? "polite" : undefined}
            className="text-sm font-medium tabular-nums text-[color:var(--g-ink-2)]"
          >
            {count ?? ""}
          </p>
          {actions}
        </div>
      </div>

      {/* The armature: drawn, not implied. */}
      <div
        aria-hidden="true"
        className="h-px w-full bg-[color:var(--g-rule-strong)]"
      />

      {children}
    </header>
  );
}
